/* UserData.store.js */

import db from '@/firebase/init'
import { scopeToChurch } from '@/helpers/churchScope'
import { assignSignInChurch, waitForUserAccess } from '@/helpers/userAccess'
import { announceSignIn } from '@/helpers/signInBroadcast'
import firebase from 'firebase'
import router from '@/router'
import { i18n } from '@/plugins/i18n'
import {
  applyFirebaseAuthLanguageForLocale,
  resolveInviteEmailLinkLanguage,
  resolveLoginEmailLinkLanguage,
} from '@/helpers/firebaseAuthLanguage'
import {
  resolveEffectiveActive,
  shouldDeactivateExpiredLicense,
} from '@/helpers/licenseExpiry'

const t = (key, params) => i18n.global.t(key, params)

function buildEmailLinkContinueUrl() {
  let continueUrl = process.env.VUE_APP_SIGNIN_URL

  if (!continueUrl && typeof window !== 'undefined') {
    const origin = window.location.origin
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    continueUrl = isLocalhost
      ? `${origin}/login`
      : origin.replace(/^http:/, 'https:') + '/login'
    continueUrl = continueUrl.replace(/\/$/, '')
  }

  if (!continueUrl) {
    continueUrl = 'https://app.thejourneycurriculum.com/login'
  }

  continueUrl = continueUrl.replace(/\/$/, '')
  if (!/\/login$/i.test(continueUrl)) {
    continueUrl = `${continueUrl}/login`
  }

  return continueUrl
}

// Watches the signed-in user's roles. Reading users needs a signed-in user
// (firestore.rules), so it is stopped before sign-out.
let stopRolesWatch = null

function stopWatchingRoles() {
  if (stopRolesWatch) stopRolesWatch()
  stopRolesWatch = null
}

function buildEmailLinkActionCodeSettings() {
  return {
    url: buildEmailLinkContinueUrl(),
    handleCodeInApp: true,
  }
}

const initialState = () => ({
  user: null,
  member: [],
  isAuthenticated: false,
  loginFeedback: null,
  signingInWithLink: false,
  oldFullPath: null,
  profileComplete: false,
  roles: null,
})

// State object
const state = initialState()

// Getter functions
const getters = {
  // member(state) {
  //     return state.member
  // },
  isAuthenticated(state) {
    return state.user !== null && state.user !== undefined
  },
  getUser(state) {
    return state.user
  },
  getRoles(state) {
    return state.roles
  },
  getLoginFeedback(state) {
    return state.loginFeedback
  },
  isSigningInWithLink(state) {
    return state.signingInWithLink
  },
  getOldFullPath(state) {
    return state.oldFullPath
  },
  getProfileComplete(state) {
    return state.profileComplete
  },
}

// Actions
const actions = {
  reset({ commit }) {
    commit('RESET')
  },
  // setMember(context, payload) {
  //     context.commit('setMember', payload)
  // },
  // updateMember(context, payload) {
  //     context.commit('updateMember', payload)
  // },
  updateUser(context, payload) {
    context.commit('updateUser', payload)
  },
  updateOldFullPath(context, payload) {
    context.commit('updateOldFullPath', payload)
  },
  updateProfileComplete(context, payload) {
    context.commit('updateProfileComplete', payload)
  },
  setLoginFeedback(context, payload) {
    context.commit('setLoginFeedback', payload)
  },
  socialLogin({ commit, dispatch }, network) {
    let provider
    if (network === 'google') {
      provider = new firebase.auth.GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account',
      })
    }
    if (network === 'facebook') {
      provider = new firebase.auth.FacebookAuthProvider()
    }
    if (network === 'twitter') {
      provider = new firebase.auth.TwitterAuthProvider()
    }

    firebase
      .auth()
      .signInWithPopup(provider)
      .then((cred) => {
        // console.log('3 ', provider.providerId, cred.user)
        commit('setUser', {
          uid: cred.user.uid, // << Just the bits we need
          email: cred.user.email,
          emailVerified: cred.user.emailVerified,
          phoneNumber: cred.user.phoneNumber,
        })
        commit('setIsAuthenticated', true)
        if (cred.user.emailVerified) {
          // console.log('before ', cred)
          // this.userLogin(cred)

          /***** Sign the user in ******/
          dispatch('userLogin', {
            uid: cred.user.uid,
            email: cred.user.email,
            emailVerified: cred.user.emailVerified,
            phoneNumber: cred.user.phoneNumber,
          })
        } else {
          commit('setLoginFeedback', {
            code: 'no-verfied email',
            message: t('auth.messages.verifyEmailFirst'),
          })
        }
      })
      .catch((err) => {
        console.log('Oops. ' + err.message)
        alert('Oops. ' + err.message)
      })
  },
  userLoginWithEmailLink({ commit }, { email }) {
    commit('setLoginFeedback', null)

    const actionCodeSettings = buildEmailLinkActionCodeSettings()
    applyFirebaseAuthLanguageForLocale(resolveLoginEmailLinkLanguage())

    console.log('Sign-in link URL being used:', actionCodeSettings.url)

    firebase
      .auth()
      .sendSignInLinkToEmail(email, actionCodeSettings)
      .then(function () {
        // The link was successfully sent. Inform the user.
        // Save the email locally so you don't need to ask the user for it again
        // if they open the link on the same device.
        window.localStorage.setItem('emailForSignIn', email)
        commit('setLoginFeedback', {
          code: 'email-link',
          message: t('auth.messages.checkEmailForLink'),
        })
      })
      .catch(function (error) {
        // Some error occurred, you can inspect the code: error.code
        console.log('error ', error)
        var user = firebase.auth().currentUser
        if (user) {
          // ✅ Ensure user is not null before deleting
          user
            .delete()
            .then(() => {
              commit('setLoginFeedback', {
                code: 'error',
                message: error.message,
              })
            })
            .catch((deleteError) => {
              commit('setLoginFeedback', {
                code: 'error',
                message: deleteError.message,
              })
            })
        } else {
          console.warn(
            'No user found to delete. Possible issue with sign-in process.'
          )
          commit('setLoginFeedback', { code: 'error', message: error.message })
        }
      })
  },
  async inviteUserWithEmailLink(
    { commit, rootState, rootGetters },
    { email: typedEmail, cid, groupID }
  ) {
    commit('setLoginFeedback', null)

    // Sign-in emails are lowercase. The invitee finds their invite by that
    // email at first sign-in, and firestore.rules let them read it only when
    // it matches exactly.
    const email = String(typedEmail || '').trim().toLowerCase()

    try {
      const pendingSnap = await scopeToChurch(
        db.collection('pendingusers').where('email', '==', email),
        { getters: rootGetters }
      ).get()

      if (!pendingSnap.empty) {
        commit('setLoginFeedback', {
          message: t('auth.messages.invitePending'),
        })
        return
      }

      const actionCodeSettings = buildEmailLinkActionCodeSettings()
      const authLanguage = await resolveInviteEmailLinkLanguage({
        cid,
        rootState,
      })
      applyFirebaseAuthLanguageForLocale(authLanguage)
      await firebase.auth().sendSignInLinkToEmail(email, actionCodeSettings)

      await db.collection('pendingusers').add({
        email: email,
        cid: cid,
        groupID: groupID,
        sent: Date.now(),
      })

      commit('setLoginFeedback', {
        message: t('auth.messages.inviteSent', { email }),
      })
    } catch (error) {
      console.log('error ', error)
      commit('setLoginFeedback', {
        code: 'error',
        message: error.message || t('auth.messages.inviteFailed'),
      })
    }
  },
  async signInWithEmailLink(
    { commit, dispatch },
    { emailLink } = {}
  ) {
    const link =
      emailLink ||
      (typeof window !== 'undefined' ? window.location.href : '')
    if (!link || !firebase.auth().isSignInWithEmailLink(link)) {
      return { ok: false, skipped: true }
    }

    // Guard against duplicate consumers trying to use the same one-time code.
    if (window.__emailLinkSignInInFlight) {
      return { ok: false, inFlight: true }
    }
    window.__emailLinkSignInInFlight = true
    // App.vue shows "Signing you in…" while this runs: the router waits for it
    // before showing any page, which can take several seconds.
    commit('setSigningInWithLink', true)

    try {
      let email = window.localStorage.getItem('emailForSignIn')
      if (!email) {
        // User opened the link on a different device.
        email = window.prompt('Please provide your email for confirmation')
      }
      if (!email || !String(email).trim()) {
        commit('setLoginFeedback', {
          code: 'error',
          message: t('auth.messages.emailRequiredForLink'),
        })
        return { ok: false, reason: 'missing-email' }
      }

      const result = await firebase
        .auth()
        .signInWithEmailLink(String(email).trim(), link)
      window.localStorage.removeItem('emailForSignIn')

      commit('setUser', {
        uid: result.user.uid, // << Just the bits we need
        email: result.user.email,
        emailVerified: result.user.emailVerified,
        phoneNumber: result.user.phoneNumber,
      })
      commit('setIsAuthenticated', true)
      if (result.user.emailVerified) {
        await dispatch('userLogin', {
          uid: result.user.uid,
          email: result.user.email,
          emailVerified: result.user.emailVerified,
          phoneNumber: result.user.phoneNumber,
        })
      } else {
        commit('setLoginFeedback', {
          code: 'no-verfied email',
          message: t('auth.messages.verifyEmailFirst'),
        })
      }
      return { ok: true }
    } catch (error) {
      console.log(error)
      commit('setLoginFeedback', {
        code: 'error',
        message: error.message || t('auth.messages.emailLinkFailed'),
      })
      return { ok: false, error }
    } finally {
      window.__emailLinkSignInInFlight = false
      commit('setSigningInWithLink', false)
    }
  },
  /************* SIGN IN WITH PHONE ***************/
  userGetPhoneVerificationCodeForLink({ commit }, { phone, appVerifier }) {
    commit('setLoginFeedback', null)

    if (!appVerifier) {
      commit('setLoginFeedback', {
        code: 'error',
        message: t('auth.messages.invalidRecaptcha'),
      })
      return
    }

    firebase
      .auth()
      .currentUser.linkWithPhoneNumber(phone, appVerifier)
      .then((confirmationResult) => {
        window.confirmationResult = confirmationResult
        commit('setLoginFeedback', {
          code: 'phone-verified',
          message: t('auth.messages.checkPhoneForCode'),
        })
      })
      .catch((error) => {
        console.error('Error sending verification code:', error)
        commit('setLoginFeedback', {
          code: 'error',
          message: error.message || t('auth.messages.sendCodeFailed'),
        })
      })
  },
  async userGetPhoneVerificationCode({ commit }, { phone }) {
    commit('setLoginFeedback', null)

    try {
      // Reset stale phone auth artifacts before sending a new code.
      window.confirmationResult = null
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear?.()
        } catch (_) {
          // best-effort cleanup
        }
        window.recaptchaVerifier = null
      }

      window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
        'sign-in-button',
        {
          size: 'invisible',
          callback: () => {},
        }
      )
      const appVerifier = window.recaptchaVerifier

      const confirmationResult = await firebase
        .auth()
        .signInWithPhoneNumber(phone, appVerifier)
      window.confirmationResult = confirmationResult

      commit('setLoginFeedback', {
        code: 'phone-verified',
        confirmation: confirmationResult,
        message: t('auth.messages.checkPhoneForCode'),
      })
      return { ok: true }
    } catch (err) {
      console.error('Error sending phone verification code:', err)
      commit('setLoginFeedback', {
        code: 'error',
        message: err?.message || t('auth.messages.sendCodeUnavailable'),
      })
      return { ok: false, reason: 'send-failed', error: err }
    }
  },
  submitPhoneNumberAuthCode({ commit, state }, { otp }) {
    commit('setLoginFeedback', null)

    if (!window.confirmationResult) {
      commit('setLoginFeedback', {
        code: 'error',
        message: t('auth.messages.sessionExpired'),
      })
      return
    }

    const verificationId = window.confirmationResult.verificationId

    const phoneCredential = firebase.auth.PhoneAuthProvider.credential(
      verificationId,
      otp
    )

    firebase
      .auth()
      .currentUser.linkWithCredential(phoneCredential)
      .then(() => {
        return firebase.auth().currentUser.reload()
      })
      .then(() => {
        const currentUser = firebase.auth().currentUser
        if (!currentUser) throw new Error('No authenticated user after reload')

        const updatedPhone = firebase.auth().currentUser.phoneNumber
        commit('updateUser', {
          ...state.user,
          phoneNumber: updatedPhone,
        })

        commit('setLoginFeedback', {
          code: 'linked-number',
          message: t('auth.messages.phoneLinked', { phone: updatedPhone }),
        })
      })
      .catch(async (error) => {
        console.warn('Phone link error:', error.code, error.message)

        // ✅ Check if it still succeeded despite the error
        try {
          await firebase.auth().currentUser.reload()
          const currentUser = firebase.auth().currentUser

          if (currentUser.phoneNumber) {
            // ✅ Success fallback
            commit('updateUser', {
              ...state.user,
              phoneNumber: currentUser.phoneNumber,
            })

            commit('setLoginFeedback', {
              code: 'linked-number',
              message: t('auth.messages.phoneLinkedNetworkError', {
                phone: currentUser.phoneNumber,
              }),
            })
          } else {
            // ⛔ Actually failed
            commit('setLoginFeedback', {
              code: 'error',
              message: error.message || t('auth.messages.linkPhoneFailed'),
            })
          }
        } catch (e) {
          console.error('Reload error after catch:', e)
          commit('setLoginFeedback', {
            code: 'error',
            message: error.message || t('auth.messages.linkPhoneFailed'),
          })
        }
      })
  },
  unLinkPhoneFromAccount({ commit }, { phone }) {
    commit('setLoginFeedback', null)

    let u = firebase.auth().currentUser

    firebase
      .auth()
      .currentUser.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID)
      .then(() => {
        // Auth provider unlinked from account
        // console.log('removing number ', u)
        let unLinkPhone = async function () {
          // async function logUsers() {
          let ref = db.collection('users').where('email', '==', u.email)
          let theUser = await ref.get()
          // console.log('theUser.docs ', theUser.docs)
          if (theUser.docs.length > 0) {
            for (const doc of theUser.docs) {
              // check our activation date to see if it is expired
              await db.collection('users').doc(doc.id).update({
                phoneNumber: null,
              })

              commit('updateUser', {
                cid: doc.data().cid,
                uid: doc.data().uid,
                url: doc.data().url,
                fullPath: doc.data().fullPath,
                user_name: doc.data().user_name,
                active: doc.data().active,
                activated: doc.data().activated,
                archived: doc.data().archived,
                id: doc.id,
                phoneNumber: null,
              })
            }
          }
        }
        try {
          unLinkPhone()
        } catch (err) {
          console.log('Error getting documents', err)
        }
        // commit('updateUser', {
        //     phoneNumber: null,
        // })

        commit('setLoginFeedback', {
          code: 'unlinked-number',
          message: t('auth.messages.phoneRemoved', { phone }),
        })
        // ...
      })
      .catch((error) => {
        // An error happened
        console.log(error)
        // ...
      })
    // console.log('unLinkPhoneFromAccount called ', phone)
  },
  signInWithPhone({ commit, dispatch }, { otp }) {
    commit('setLoginFeedback', null)
    if (!window.confirmationResult) {
      commit('setLoginFeedback', {
        code: 'no-verification-code',
        message: t('auth.messages.smsSessionExpired'),
      })
      return
    }

    window.confirmationResult
      .confirm(otp)
      .then(async (result) => {
        // A number that is not linked to an account signs in to a new, empty
        // account, which then holds the number and blocks linking it to the
        // real account (GitHub #114). Remove it and send the person to email.
        const isNewUser = result.additionalUserInfo?.isNewUser === true
        if (isNewUser || !result.user.email) {
          if (isNewUser) {
            await result.user
              .delete()
              .catch((err) => console.warn('Could not remove unlinked phone account:', err))
          }
          await firebase.auth().signOut()
          commit('setLoginFeedback', {
            code: 'phone-not-linked',
            message: t('auth.messages.phoneNotLinked'),
          })
          return
        }

        // User signed in successfully.
        // commit('setLoginFeedback', { code: 'linked-number', message: result.user.phoneNumber + ' Has been successfully linked to your account.' });

        // console.log('result ', result)

        commit('setIsAuthenticated', true)

        commit('setUser', {
          uid: result.user.uid, // << Just the bits we need
          email: result.user.email,
          emailVerified: result.user.emailVerified,
          phoneNumber: result.user.phoneNumber,
        })
        commit('setIsAuthenticated', true)
        if (result.user.emailVerified) {
          // console.log('verfied')
          // we are verified so add to the database

          /***** Sign the user in ******/
          dispatch('userLogin', {
            uid: result.user.uid,
            email: result.user.email,
            emailVerified: result.user.emailVerified,
            phoneNumber: result.user.phoneNumber,
          })
        } else {
          // console.log('not verified')
          commit('setLoginFeedback', {
            code: 'no-verfied email',
            message: t('auth.messages.verifyEmailFirst'),
          })
        }
        // ...
      })
      .catch((error) => {
        // User couldn't sign in (bad verification code?)
        console.log('error ', error)
        commit('setLoginFeedback', {
          code: 'no-verification-code',
          message: t('auth.messages.smsCodeInvalid'),
        })
        // ...
      })
  },
  verifyUsersEmail({ commit }) {
    commit('setLoginFeedback', null)
    var actionCodeSettings = {
      url: buildEmailLinkContinueUrl(),
    }
    let user = firebase.auth().currentUser
    if (user) {
      // send verification email
      user
        .sendEmailVerification(actionCodeSettings)
        .then(function () {
          // Email sent.
          // console.log('email sent ', cred)
          commit('setLoginFeedback', {
            message: t('auth.messages.verificationEmailSent'),
          })
        })
        .catch(function (error) {
          // An error happened.
          console.log('error ', error)
        })
    }
  },
  userLogin(
    { commit, dispatch },
    { uid, email, emailVerified, phoneNumber, announce = true }
  ) {
    console.log('we found userLogin ', uid, email, emailVerified, phoneNumber)
    let logUsers = async function () {
      // async function logUsers() {
      let ref = db.collection('users').where('email', '==', email)
      let theUser = await ref.get()
      // console.log('theUser.docs ', theUser.docs)
      if (theUser.docs.length == 0) {
        let defaultRoles = ['member']

        // Church admin emails are stored lowercase (emails are case-insensitive)
        let churchAdminRef = db
          .collection('churches')
          .where('churchadminemail', 'in', [
            ...new Set([email, String(email).trim().toLowerCase()]),
          ])
        let isAdmin = await churchAdminRef.get()
        if (isAdmin.docs.length > 0) {
          defaultRoles.push('admin')
        }

        // create a user name from the email address
        const e = email
        const username = e ? e.split('@')[0] : ''

        // The profile starts without a church; the server sets it from the
        // user's invite (and joins its group) or the church's admin email
        // (GitHub #147).
        await db.collection('users').add({
          url: null,
          fullPath: null,
          roles: defaultRoles,
          uid: uid,
          email: email,
          emailVerified: emailVerified,
          user_name: username,
          phoneNumber: phoneNumber,
          cid: null,
          active: null,
          joined: Date.now(),
        })
        console.log('1 userLogin')

        const cid = await assignSignInChurch()

        commit('updateUser', {
          cid: cid,
          uid: uid,
          url: null,
          fullPath: null,
          user_name: username,
          roles: defaultRoles,
        })

        // The function writes userAccess, but syncUserAccess (run for the new
        // profile) can briefly overwrite it; church reads need the church.
        if (cid && !(await waitForUserAccess(uid, cid))) {
          console.warn('userAccess has not caught up with the church yet', uid, cid)
        }
        if (cid) {
          await dispatch('AppData/hydrateChurchContextFromCid', cid, {
            root: true,
          })
        }
      } else {
        for (const doc of theUser.docs) {
          // console.log('else ', doc.data());

          const userData = doc.data()
          let activeUser = resolveEffectiveActive(userData)

          if (shouldDeactivateExpiredLicense(userData)) {
            await db.collection('users').doc(doc.id).update({ active: null })
          }

          if (phoneNumber) {
            await db.collection('users').doc(doc.id).update({
              phoneNumber: phoneNumber,
            })
          }

          await firebase.auth().currentUser.reload()
          const currentUser = firebase.auth().currentUser

          commit('updateUser', {
            cid: doc.data().cid,
            uid: doc.data().uid,
            url: doc.data().url,
            fullPath: doc.data().fullPath,
            user_name: doc.data().user_name,
            active: activeUser,
            activated: doc.data().activated,
            archived: doc.data().archived,
            id: doc.id,
            phoneNumber:
              currentUser.phoneNumber ||
              doc.data().phoneNumber ||
              phoneNumber ||
              '',
            roles: doc.data().roles || [],
            possibleLeader: doc.data().possibleLeader,
          })

          const userCid = doc.data().cid
          if (userCid) {
            await dispatch('AppData/hydrateChurchContextFromCid', userCid, {
              root: true,
            })
          }
        }
      }

      // console.log('5 next step')
      dispatch('AppData/getSections', {}, { root: true })
      // dispatch('AppData/getUnits', {}, { root: true })
      dispatch('AppData/getMissions', {}, { root: true })
      // dispatch('AppData/getWeeks', {}, { root: true })

      // get user's current roles
      let uRef = db.collection('users').where('email', '==', email)
      stopWatchingRoles()
      stopRolesWatch = uRef.onSnapshot(
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            let docs = change.doc
            commit('setRoles', docs.data().roles)
          })
        },
        (err) => {
          // Other sign-out paths (maintenance, NavBar) end it with a denied read.
          if (firebase.auth().currentUser) console.error('Roles listener failed:', err)
          stopRolesWatch = null
        }
      )

      router.push({ name: 'Home' })
      // A login page open in another tab moves on too (Login.vue), unless
      // this sign-in is itself following another tab.
      if (announce) announceSignIn(uid)
    }
    try {
      logUsers()
    } catch (err) {
      console.log('Error getting documents', err)
    }
  },
  userSignOut({ commit }) {
    commit('setLoginFeedback', null)

    // 🧹 Full cleanup
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear?.()
      } catch (e) {
        console.warn('recaptchaVerifier.clear() failed during logout:', e)
      }
      window.recaptchaVerifier = null
      window.recaptchaVerifierWidgetId = undefined
    }

    // Optional: Reset grecaptcha if loaded
    if (typeof window.grecaptcha !== 'undefined') {
      try {
        window.grecaptcha.reset()
      } catch (e) {
        console.warn('grecaptcha.reset failed:', e)
      }
    }

    // Clear DOM in case it persists
    const container = document.getElementById('recaptcha-container')
    if (container) container.innerHTML = ''

    // Reset confirmation result (in case user cancels mid-process)
    window.confirmationResult = null

    stopWatchingRoles()
    firebase
      .auth()
      .signOut()
      .then(() => {
        commit('RESET')
        router.push({ name: 'Login' })
      })
      .catch((err) => {
        commit('RESET')
        commit('setLoginFeedback', err)
        router.push({ name: 'Login' })
      })
  },
  resetUserPW({ commit }, { email }) {
    commit('setLoginFeedback', null)
    firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        // commit('setUser', null);
        // commit('setIsAuthenticated', false);
        commit('setLoginFeedback', {
          message: t('auth.messages.passwordResetSent', { email }),
        })
        // router.push({ name: 'Login' })
      })
      .catch((err) => {
        commit('setUser', null)
        commit('setIsAuthenticated', false)
        commit('setLoginFeedback', err)
        // router.push({ name: 'Login' })
      })
  },
  isAuthorized({ state }, permissions) {
    const userRoles = state.roles
    // console.log('2 ', userRoles)
    if (permissions && userRoles) {
      const found = userRoles.some((r) => permissions.includes(r))
      return found
    }
  },
}

// Mutations
const mutations = {
  RESET(state) {
    const newState = initialState()
    // console.log('reset called 2', newState)
    Object.keys(newState).forEach((key) => {
      state[key] = newState[key]
    })

    // Explicitly reset critical fields
    state.roles = []
    state.user = {
      uid: null,
      email: '',
      emailVerified: false,
      phoneNumber: '',
      user_name: '',
      fullPath: '',
      url: '',
      roles: [],
    }
  },
  // setMember(state, payload) {
  //     state.member = payload
  // },
  // updateMember(state, payload) {
  //     state.member.signed = payload.signed
  // },
  removeUser(state) {
    state.user = null
  },
  setUser(state, payload) {
    // console.log("🧠 Committing setUser with payload:", payload);

    if (!payload || typeof payload !== 'object') {
      state.user = {
        uid: null,
        email: '',
        emailVerified: false,
        phoneNumber: '',
        user_name: '',
        fullPath: '',
        url: '',
        roles: [],
      }
      state.roles = []
      return
    }

    // 🛡️ Guard null state.user
    const oldUser =
      typeof state.user === 'object' && state.user !== null ? state.user : {}

    state.user = {
      ...oldUser,
      uid: payload.uid || oldUser.uid || null,
      email: payload.email ? payload.email.trim() : oldUser.email || '',
      emailVerified:
        payload.emailVerified !== undefined
          ? payload.emailVerified
          : oldUser.emailVerified || false,
      phoneNumber:
        payload.phoneNumber !== undefined
          ? payload.phoneNumber
          : oldUser.phoneNumber || '',
      user_name: payload.user_name || oldUser.user_name || '',
      fullPath: payload.fullPath || oldUser.fullPath || '',
      url: payload.url || oldUser.url || '',
      roles: Array.isArray(payload.roles) ? payload.roles : oldUser.roles || [],
    }

    state.roles = state.user.roles

    // console.log("🧠 Updated Vuex user state:", state.user);
  },
  updateUser(state, payload) {
    if (!state.user) {
      state.user = {} // Or reset with a base structure if needed
    }
    if (!payload || typeof payload !== 'object') return

    const keys = [
      'cid',
      'uid',
      'url',
      'fullPath',
      'user_name',
      'active',
      'activated',
      'archived',
      'id',
      'phoneNumber',
      'email',
      'emailVerified',
      'possibleLeader',
    ]
    keys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        state.user[key] = payload[key]
      }
    })

    if (payload.roles) {
      state.user.roles = payload.roles
      state.roles = payload.roles
    }
  },
  setRoles(state, payload) {
    state.roles = payload
  },
  updateOldFullPath(state, payload) {
    state.oldFullPath = payload
  },
  updateProfileComplete(state, payload) {
    state.profileComplete = payload
  },
  setIsAuthenticated(state, payload) {
    state.isAuthenticated = payload
  },
  setLoginFeedback(state, payload) {
    state.loginFeedback = payload
  },
  setSigningInWithLink(state, payload) {
    state.signingInWithLink = payload
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
